<template>
  <main class="manage-features" v-if="features">
    <!-- Header -->
    <div class="manage-features__header">
      <div class="manage-features__header-content">
        <h1 class="manage-features__title">Manage Features</h1>
        <p class="manage-features__subtitle">Enable or disable platform features</p>
      </div>
      <span class="manage-features__badge">
        {{ orgFeatures.length }} of {{ features.length }} active
      </span>
    </div>

    <!-- Features List -->
    <div class="manage-features__content">
      <div
        class="manage-features__card"
        v-for="feature in features"
        :key="feature._id"
      >
        <div class="manage-features__card-info">
          <h3 class="manage-features__card-title">{{ feature.name }}</h3>
          <p class="manage-features__card-description">{{ feature.description }}</p>
          <a
            v-can="'deal:addDeal'"
            v-if="
              feature.name === 'Public Raise' &&
              orgFeatures.includes('PUBLIC_RAISE_PENDING')
            "
            href="https://airtable.com/shrNQr1FF3Io52cCO"
            target="_blank"
            rel="noopener noreferrer"
            class="manage-features__card-link"
          >
            <span>Complete Due Dilligence Form</span>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 12L10 8L6 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </a>
        </div>
        <div class="manage-features__card-action">
          <div
            v-if="
              feature.status === 'ACTIVE' &&
              orgFeatures.includes(feature.category)
            "
            class="manage-features__status manage-features__status--active"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M13.333 4L5.99967 11.3333L2.66634 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Active
          </div>
          <div
            class="manage-features__status manage-features__status--coming"
            v-if="feature.status !== 'ACTIVE'"
          >
            Coming soon
          </div>
          <button
            v-if="
              feature.status === 'ACTIVE' &&
              !orgFeatures.includes(feature.category)
            "
            @click="addFeature(feature.category)"
            class="manage-features__activate-btn"
            :class="{
              'tw-hidden':
                (orgFeatures.includes('PUBLIC_RAISE_PENDING') &&
                  feature.name === 'Public Raise') ||
                (orgFeatures.includes('PUBLIC_RAISE_REVIEW') &&
                  feature.name === 'Public Raise')
            }"
          >
            Activate
          </button>
          <div
            v-if="
              (orgFeatures.includes('PUBLIC_RAISE_PENDING') &&
                feature.name === 'Public Raise') ||
              (orgFeatures.includes('PUBLIC_RAISE_REVIEW') &&
                feature.name === 'Public Raise')
            "
            class="manage-features__status"
            :class="[
              orgFeatures.includes('PUBLIC_RAISE_PENDING')
                ? 'manage-features__status--pending'
                : 'manage-features__status--review',
            ]"
          >
            {{
              orgFeatures.includes("PUBLIC_RAISE_PENDING")
                ? "Pending"
                : "In Review"
            }}
          </div>
        </div>
      </div>
    </div>

    <!-- Diligence Modal -->
    <SmallModal
      v-if="showDiligenceModal"
      class="tw-z-50"
      @close="showDiligenceModal = !showDiligenceModal"
    >
      <template v-slot:content>
        <div class="manage-features__modal">
          <div class="manage-features__modal-icon">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <path d="M16 10.6667V16M16 21.3333H16.0133M28 16C28 22.6274 22.6274 28 16 28C9.37258 28 4 22.6274 4 16C4 9.37258 9.37258 4 16 4C22.6274 4 28 9.37258 28 16Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <h3 class="manage-features__modal-title">
            You are about to start raising publicly
          </h3>
          <p class="manage-features__modal-text">
            A due diligence form will be sent to your email address. Public raise
            will be activated when you have filled this form and it has been approved.
          </p>
          <div class="manage-features__modal-actions">
            <button
              class="manage-features__modal-cancel"
              @click="showDiligenceModal = false"
            >
              Cancel
            </button>
            <button
              class="manage-features__modal-confirm"
              @click="addFeature('PUBLIC_RAISE')"
            >
              Fill Due Diligence Form
            </button>
          </div>
        </div>
      </template>
    </SmallModal>
  </main>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import SmallModal from "@/components/general/SmallModal.vue";
import Btn from "@/components/general/BtnComponent.vue";

export default {
  name: "ManageFeatures",

  components: {
    SmallModal,
    Btn,
  },

  data() {
    return {
      showDiligenceModal: false,
    };
  },

  computed: {
    ...mapGetters("organisationModule", ["features", "organisationDetails"]),
    orgFeatures() {
      return this.organisationDetails?.products.split(" ");
    },
    userFeatures() {
      let userFeatures = [];
      this.orgFeatures.forEach((userFeature) => {
        let featureObj = this.features.filter(
          (filter) => filter.category === userFeature
        );
        userFeatures.push(featureObj[0]);
      });
      return userFeatures;
    },
  },
  created() {
    this.getFeatures();
  },
  methods: {
    ...mapActions("organisationModule", [
      "getFeatures",
      "subscribeToFeature",
      "getOrganisationProfile",
    ]),
    ...mapActions(["showToast"]),
    async addFeature(feature) {
      if (
        feature === "PUBLIC_RAISE" &&
        this.orgFeatures.includes("PUBLIC_RAISE_PENDING")
      ) {
        return;
      }
      if (feature === "PUBLIC_RAISE" && !this.showDiligenceModal) {
        this.showDiligenceModal = true;
        return;
      }
      if (feature === "PUBLIC_RAISE") {
        window.open("https://airtable.com/shrNQr1FF3Io52cCO", "_blank");
      }
      try {
        const response = await this.subscribeToFeature([feature]);
        const { message } = response.data;
        this.showToast({
          description: message,
          display: true,
          type: "success",
        });
        this.getOrganisationProfile();
        this.showDiligenceModal = false;
      } catch (error) {
        return error;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.manage-features {
  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 24px;
    border-bottom: 1px solid #E2E8F0;
    gap: 16px;
    flex-wrap: wrap;
  }

  &__header-content {
    flex: 1;
    min-width: 200px;
  }

  &__title {
    font-family: 'DM Sans', sans-serif;
    font-size: 18px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 4px 0;
  }

  &__subtitle {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    margin: 0;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    padding: 8px 12px;
    background: #FEF3C7;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: #B45309;
  }

  &__content {
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    padding: 20px;
    background: #F8FAFC;
    border: 1px solid #E2E8F0;
    border-radius: 12px;
    flex-wrap: wrap;
  }

  &__card-info {
    flex: 1;
    min-width: 200px;
    max-width: 400px;
  }

  &__card-title {
    font-family: 'DM Sans', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 6px 0;
    text-transform: capitalize;
  }

  &__card-description {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    margin: 0;
    line-height: 1.5;
  }

  &__card-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 12px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #4D84FF;
    text-decoration: none;
    transition: color 0.15s ease;

    &:hover {
      color: #3B6FE8;
    }
  }

  &__card-action {
    flex-shrink: 0;
  }

  &__status {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 500;
    min-width: 100px;

    &--active {
      background: #DCFCE7;
      color: #16A34A;
    }

    &--coming {
      background: #FEE2E2;
      color: #DC2626;
    }

    &--pending {
      background: #FEF3C7;
      color: #B45309;
    }

    &--review {
      background: #DBEAFE;
      color: #2563EB;
    }
  }

  &__activate-btn {
    padding: 10px 20px;
    background: #4D84FF;
    border: none;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: white;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: #3B6FE8;
    }
  }

  &__modal {
    text-align: center;
    padding: 8px;
  }

  &__modal-icon {
    width: 64px;
    height: 64px;
    background: #FEF3C7;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
    color: #B45309;
  }

  &__modal-title {
    font-family: 'DM Sans', sans-serif;
    font-size: 18px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 12px 0;
  }

  &__modal-text {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    line-height: 1.5;
    margin: 0 0 24px 0;
  }

  &__modal-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
  }

  &__modal-cancel {
    padding: 10px 20px;
    background: white;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #64748B;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      border-color: #CBD5E1;
      color: #0F172A;
    }
  }

  &__modal-confirm {
    padding: 10px 20px;
    background: #4D84FF;
    border: none;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: white;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: #3B6FE8;
    }
  }
}
</style>
